/** Teksty widoku Finanse → Cash flow (`cf.*`). */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const cfPl = {
  nav: 'Cash flow',
  title: 'Cash flow',
  description: 'Kasy, bank, zobowiązania i prognoza płynności na najbliższe tygodnie — z dokumentów InsERT GT.',
  noData: 'brak danych',
  hero: { cash: 'Kasy', inflowsWeek: 'Wpływy w tym tygodniu', due14: 'Do zapłaty w 14 dni' },
  tabs: { overview: 'Przegląd', cash: 'Kasa', bank: 'Bank', payables: 'Zobowiązania', inflows: 'Wpływy w okresach' },
  demo: {
    badge: 'dane przykładowe',
    hint: 'Część pozycji to dane przykładowe (nakładka demo) — uzupełniają to, czego nie ma w ERP.',
  },
  month: { prev: 'Poprzedni miesiąc', next: 'Następny miesiąc', current: 'Bieżący miesiąc' },
  category: {
    CUSTOMERS: 'Klienci',
    SUPPLIERS: 'Dostawcy',
    WAGES: 'Wynagrodzenia',
    TAXES: 'Podatki i ZUS',
    OTHER: 'Inne',
    TRANSFER: 'Przeksięgowania własne',
  },
  tiles: {
    cashBalance: 'Saldo kas',
    cashBalanceHint: 'Wszystkie kasy na dziś',
    inflowsWeek: 'Wpływy w tygodniu',
    vsPrevWeek: 'poprzedni: {amount}',
    due14: 'Do zapłaty w 14 dni',
    overdue: 'w tym zaległe: {amount}',
    balanceAfter: 'Saldo za {weeks} tyg.',
    balanceStart: 'Dziś: {amount} (kasy + rachunki)',
    balanceCashOnly: 'Tylko kasy — wpisz stan rachunków bankowych',
  },
  chart: {
    title: 'Wpływy i wypływy tydzień po tygodniu',
    subtitle: '12 tygodni wstecz i prognoza na {weeks} tyg. od bieżącego tygodnia (należności minus zobowiązania)',
    inflow: 'Wpływy',
    outflow: 'Wypływy',
    balance: 'Środki na koniec tygodnia',
    net: 'Saldo tygodnia',
    forecast: 'prognoza',
    backlog: 'w tym zaległości rozłożone na horyzont',
    weekOf: 'Tydzień od',
    horizon: 'Horyzont prognozy',
    weeksShort: '{weeks} tyg.',
  },
  assumptions: 'Założenia prognozy: klienci płacą średnio {customer} dni po terminie, my — {ours} dni po terminie.',
  dataUntil: { cash: 'Kasa do', bank: 'Bank do', statements: 'Wyciągi do', payables: 'Zobowiązania do' },
  flows: {
    title: 'Wpływy i wypływy wg kategorii',
    subtitle: 'Dokumenty KP, KW, BP i BW z Subiekta; przeksięgowania własne poza sumami',
    channel: 'Źródło',
    channels: { ALL: 'Razem', CASH: 'Kasa', BANK: 'Bank' },
    granularity: 'Okres',
    granularities: { week: 'Tygodnie', month: 'Miesiące' },
    period: 'Okres',
    inflowTotal: 'Wpływy',
    outflowTotal: 'Wypływy',
    net: 'Saldo',
    rules:
      'Kategorie: wpłaty od firm i osób = Klienci; wypłaty do firm = Dostawcy; pracownicy, przelewy do osób fizycznych i tytuły „wynagrodzenie” = Wynagrodzenia (bez nazwisk); urzędy, ZUS i tytuły podatkowe = Podatki i ZUS; opłaty, odsetki = Inne; wpłata i wypłata tego samego dnia na tę samą kwotę bez kontrahenta = przeksięgowanie własne.',
  },
  bankBalance: {
    add: 'Wpisz stan rachunków',
    title: 'Stan rachunków bankowych',
    description: 'Suma wszystkich rachunków firmy na koniec wybranego dnia. Kolejne dni liczymy z ruchów bankowych (BP / BW).',
    date: 'Na koniec dnia',
    amount: 'Kwota (zł)',
    note: 'Notatka (opcjonalnie)',
    privacy: 'Nie wpisuj numerów rachunków — wystarczy łączna kwota.',
    amountInvalid: 'Podaj kwotę, np. 1 250 000,00',
    dateInvalid: 'Data nie może być z przyszłości',
    saved: 'Zapisano stan rachunków',
    saveError: 'Nie udało się zapisać stanu rachunków',
    missingTitle: 'Brak stanu rachunków bankowych',
    missingBody: 'Subiekt nie podaje wiarygodnego salda rachunków. Bez niego „Saldo za N tygodni” liczy tylko kasy.',
    entered: 'Stan rachunków z {date}: {amount}',
  },
  cash: {
    main: 'główna',
    lastMovement: 'ostatni ruch: {date}',
    noMovements: 'brak ruchów',
    reportTitle: 'Raport kasowy dzienny',
    reportSubtitle: 'Wpłaty (KP), wypłaty (KW) i saldo na koniec dnia · razem we wszystkich kasach: {total}',
    register: 'Kasa',
    allRegisters: 'Wszystkie kasy',
    opening: 'Saldo otwarcia',
    closing: 'Saldo zamknięcia',
    inflow: 'Wpłaty',
    outflow: 'Wypłaty',
    balance: 'Saldo',
    day: 'Dzień',
    documents: 'Dokumenty',
    empty: 'Brak dokumentów kasowych w tym miesiącu',
  },
  bank: {
    summaryTitle: 'Rachunki bankowe',
    balanceKnown: 'Stan wpisany na {date}: {amount}, dalej z ruchów bankowych',
    balanceUnknown: 'Stan rachunków nie został wpisany — pokazujemy same ruchy',
    balanceToday: 'Stan na dziś',
    monthInflow: 'Wpływy w miesiącu',
    monthOutflow: 'Wypływy w miesiącu',
    linesTitle: 'Pozycje wyciągów',
    search: 'Szukaj: kontrahent, tytuł, faktura',
    category: 'Kategoria',
    allCategories: 'Wszystkie kategorie',
    direction: 'Kierunek',
    directions: { ALL: 'Wszystkie', IN: 'Wpływy', OUT: 'Wypływy' },
    date: 'Data',
    counterparty: 'Kontrahent',
    title: 'Tytuł',
    amount: 'Kwota',
    invoice: 'faktura {no}',
    anonymized: 'Wynagrodzenia / osoba prywatna',
    anonymizedHint: 'Przelew do lub od osoby fizycznej — bez nazwiska i tytułu (ochrona danych).',
    empty: 'Brak pozycji wyciągów dla wybranych filtrów',
  },
  payables: {
    title: 'Zobowiązania wg dostawców',
    total: 'Zobowiązania razem',
    documents: '{count} dokumentów',
    avgTerm: 'Średni termin płatności',
    avgTermHint: 'Od wystawienia do terminu, 12 mies.',
    paidAfter: 'Płacimy średnio po',
    paidAfterHint: 'Od wystawienia do zapłaty, 12 mies.',
    days: '{days} dni',
    search: 'Szukaj dostawcy',
    supplier: 'Dostawca',
    sum: 'Razem',
    term: 'Termin',
    oldestDue: 'Najstarszy termin',
    allSuppliers: 'Wszyscy dostawcy',
    showAll: 'Pokaż wszystkich ({count})',
    showLess: 'Pokaż mniej',
    empty: 'Brak otwartych zobowiązań',
    buckets: { overdue: 'Zaległe', d7: 'Do 7 dni', d14: 'Do 14 dni', d30: 'Do 30 dni', later: 'Później' },
    kind: { COMPANY: 'Firma', PERSON: 'Osoba prywatna', EMPLOYEE: 'Wynagrodzenia', OFFICE: 'Urzędy i ZUS', OTHER: 'Inne' },
  },
  inflows: {
    open: 'Należności otwarte',
    overdue: 'zaległe: {amount}',
    inWindow: 'Oczekiwane w {weeks} tyg.',
    backlogHint: 'w tym zaległości rozłożone: {amount}',
    avgDelay: 'Średnie opóźnienie klientów',
    avgDelayHint: 'Dni po terminie, ważone kwotą, 180 dni',
    outside: 'Poza prognozą',
    outsideHint: 'wątpliwe (ponad {days} dni po terminie): {doubtful} · po horyzoncie: {beyond}',
    chartTitle: 'Oczekiwane wpływy tydzień po tygodniu',
    chartSubtitle: 'Wg terminów płatności i skorygowane średnim opóźnieniem każdego klienta',
    byDue: 'Wg terminów',
    expected: 'Skorygowane opóźnieniem',
    backlog: 'Zaległości rozłożone na horyzont',
    customersTitle: 'Najwięksi płatnicy w horyzoncie',
    customersSubtitle: 'Klienci z największymi oczekiwanymi wpływami',
    customer: 'Klient',
    openCol: 'Otwarte',
    overdueCol: 'Zaległe',
    delayCol: 'Śr. opóźnienie',
    expectedCol: 'Oczekiwane w {weeks} tyg.',
  },
};

export const cfEn: Shape<typeof cfPl> = {
  nav: 'Cash flow',
  title: 'Cash flow',
  description: 'Cash registers, bank, payables and the liquidity forecast for the coming weeks — from InsERT GT documents.',
  noData: 'no data',
  hero: { cash: 'Cash', inflowsWeek: 'Inflows this week', due14: 'Due in 14 days' },
  tabs: { overview: 'Overview', cash: 'Cash', bank: 'Bank', payables: 'Payables', inflows: 'Expected inflows' },
  demo: {
    badge: 'sample data',
    hint: 'Some rows are sample data (demo overlay) — they fill in what the ERP does not have.',
  },
  month: { prev: 'Previous month', next: 'Next month', current: 'Current month' },
  category: {
    CUSTOMERS: 'Customers',
    SUPPLIERS: 'Suppliers',
    WAGES: 'Wages',
    TAXES: 'Taxes and social security',
    OTHER: 'Other',
    TRANSFER: 'Own transfers',
  },
  tiles: {
    cashBalance: 'Cash balance',
    cashBalanceHint: 'All cash registers today',
    inflowsWeek: 'Inflows this week',
    vsPrevWeek: 'previous: {amount}',
    due14: 'Due in 14 days',
    overdue: 'of which overdue: {amount}',
    balanceAfter: 'Balance in {weeks} wks',
    balanceStart: 'Today: {amount} (cash + bank)',
    balanceCashOnly: 'Cash only — enter the bank balance',
  },
  chart: {
    title: 'Inflows and outflows week by week',
    subtitle: '12 weeks back and a {weeks}-week forecast from the current week (receivables minus payables)',
    inflow: 'Inflows',
    outflow: 'Outflows',
    balance: 'Funds at week end',
    net: 'Week net',
    forecast: 'forecast',
    backlog: 'incl. backlog spread over the horizon',
    weekOf: 'Week of',
    horizon: 'Forecast horizon',
    weeksShort: '{weeks} wks',
  },
  assumptions: 'Forecast assumptions: customers pay {customer} days after the due date on average, we pay {ours} days after.',
  dataUntil: { cash: 'Cash until', bank: 'Bank until', statements: 'Statements until', payables: 'Payables until' },
  flows: {
    title: 'Inflows and outflows by category',
    subtitle: 'KP, KW, BP and BW documents from Subiekt; own transfers excluded from totals',
    channel: 'Source',
    channels: { ALL: 'Total', CASH: 'Cash', BANK: 'Bank' },
    granularity: 'Period',
    granularities: { week: 'Weeks', month: 'Months' },
    period: 'Period',
    inflowTotal: 'Inflows',
    outflowTotal: 'Outflows',
    net: 'Net',
    rules:
      'Categories: receipts from companies and persons = Customers; payments to companies = Suppliers; employees, transfers to private persons and “wages” titles = Wages (no names); tax offices, social security and tax titles = Taxes; fees, interest = Other; an inflow and outflow of the same day and amount without a counterparty = own transfer.',
  },
  bankBalance: {
    add: 'Enter bank balance',
    title: 'Bank account balance',
    description: 'Total of all company accounts at the end of the chosen day. Later days are computed from bank movements (BP / BW).',
    date: 'At the end of',
    amount: 'Amount (PLN)',
    note: 'Note (optional)',
    privacy: 'Do not enter account numbers — the total amount is enough.',
    amountInvalid: 'Enter an amount, e.g. 1,250,000.00',
    dateInvalid: 'The date cannot be in the future',
    saved: 'Bank balance saved',
    saveError: 'Could not save the bank balance',
    missingTitle: 'No bank balance',
    missingBody: 'Subiekt has no reliable account balance. Without it, “Balance in N weeks” counts cash only.',
    entered: 'Bank balance of {date}: {amount}',
  },
  cash: {
    main: 'main',
    lastMovement: 'last movement: {date}',
    noMovements: 'no movements',
    reportTitle: 'Daily cash report',
    reportSubtitle: 'Receipts (KP), payments (KW) and end-of-day balance · all registers together: {total}',
    register: 'Cash register',
    allRegisters: 'All registers',
    opening: 'Opening balance',
    closing: 'Closing balance',
    inflow: 'Receipts',
    outflow: 'Payments',
    balance: 'Balance',
    day: 'Day',
    documents: 'Documents',
    empty: 'No cash documents this month',
  },
  bank: {
    summaryTitle: 'Bank accounts',
    balanceKnown: 'Balance entered for {date}: {amount}, then from bank movements',
    balanceUnknown: 'No bank balance entered — movements only',
    balanceToday: 'Balance today',
    monthInflow: 'Inflows this month',
    monthOutflow: 'Outflows this month',
    linesTitle: 'Statement lines',
    search: 'Search: counterparty, title, invoice',
    category: 'Category',
    allCategories: 'All categories',
    direction: 'Direction',
    directions: { ALL: 'All', IN: 'Inflows', OUT: 'Outflows' },
    date: 'Date',
    counterparty: 'Counterparty',
    title: 'Title',
    amount: 'Amount',
    invoice: 'invoice {no}',
    anonymized: 'Wages / private person',
    anonymizedHint: 'Transfer to or from a private person — no name and no title (data protection).',
    empty: 'No statement lines for the selected filters',
  },
  payables: {
    title: 'Payables by supplier',
    total: 'Total payables',
    documents: '{count} documents',
    avgTerm: 'Average payment term',
    avgTermHint: 'Issue to due date, 12 months',
    paidAfter: 'We pay on average after',
    paidAfterHint: 'Issue to payment, 12 months',
    days: '{days} days',
    search: 'Search supplier',
    supplier: 'Supplier',
    sum: 'Total',
    term: 'Term',
    oldestDue: 'Oldest due date',
    allSuppliers: 'All suppliers',
    showAll: 'Show all ({count})',
    showLess: 'Show less',
    empty: 'No open payables',
    buckets: { overdue: 'Overdue', d7: 'Within 7 days', d14: 'Within 14 days', d30: 'Within 30 days', later: 'Later' },
    kind: { COMPANY: 'Company', PERSON: 'Private person', EMPLOYEE: 'Wages', OFFICE: 'Tax offices and social security', OTHER: 'Other' },
  },
  inflows: {
    open: 'Open receivables',
    overdue: 'overdue: {amount}',
    inWindow: 'Expected in {weeks} wks',
    backlogHint: 'incl. spread backlog: {amount}',
    avgDelay: 'Average customer delay',
    avgDelayHint: 'Days after due, amount-weighted, 180 days',
    outside: 'Outside the forecast',
    outsideHint: 'doubtful (over {days} days overdue): {doubtful} · after the horizon: {beyond}',
    chartTitle: 'Expected inflows week by week',
    chartSubtitle: 'By due dates and adjusted by each customer’s average delay',
    byDue: 'By due date',
    expected: 'Delay-adjusted',
    backlog: 'Backlog spread over the horizon',
    customersTitle: 'Largest payers in the horizon',
    customersSubtitle: 'Customers with the largest expected inflows',
    customer: 'Customer',
    openCol: 'Open',
    overdueCol: 'Overdue',
    delayCol: 'Avg delay',
    expectedCol: 'Expected in {weeks} wks',
  },
};
